import React, { useState, useEffect } from 'react';
import questionsData from '../data/questions.json';
import './Quiz.css';

interface Question {
  id: number;
  level: number;
  question: string;
  option_a: string;
  option_b: string;
  option_c: string;
  option_d: string;
  correct_answer: string;
  user_answer?: string;
  is_correct?: boolean;
}

interface QuestionsData {
  level: number;
  questions: Question[];
}

const Quiz: React.FC = () => {
  const [currentLevel, setCurrentLevel] = useState(1);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState<{ [key: number]: string }>({});
  const [isReviewMode, setIsReviewMode] = useState(false);
  const [showResult, setShowResult] = useState(false);
  const [result, setResult] = useState<{ passed: boolean; correctCount: number; totalQuestions: number } | null>(null);
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);

  useEffect(() => {
    loadQuestions(currentLevel);
  }, [currentLevel]);

  const loadQuestions = (level: number) => {
    // 从本地JSON文件加载题目数据
    const levelData = (questionsData as QuestionsData[]).find(
      (data) => data.level === level
    );
    if (levelData) {
      const questionsWithLevel = levelData.questions.map((q) => ({
        ...q,
        level,
      }));
      setQuestions(questionsWithLevel);
      setCurrentIndex(0);
      setAnswers({});
      setIsReviewMode(false);
      setShowResult(false);
      setResult(null);
    }
  };

  const handleAnswer = (answer: string) => {
    if (!isReviewMode) {
      const newAnswers = { ...answers, [questions[currentIndex].id]: answer };
      setAnswers(newAnswers);
      
      // 立即更新当前题目的对错状态
      const updatedQuestions = questions.map((q, idx) => {
        if (idx === currentIndex) {
          const isCorrect = answer === q.correct_answer;
          return {
            ...q,
            user_answer: answer,
            is_correct: isCorrect,
          };
        }
        return q;
      });
      setQuestions(updatedQuestions);
    }
  };

  const handleNext = () => {
    if (currentIndex < questions.length - 1) {
      setCurrentIndex(currentIndex + 1);
    }
  };

  const handlePrev = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  };

  const handleSwipe = (direction: 'left' | 'right') => {
    if (direction === 'left' && currentIndex < questions.length - 1) {
      handleNext();
    } else if (direction === 'right' && currentIndex > 0) {
      handlePrev();
    }
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe) {
      handleSwipe('left');
    } else if (isRightSwipe) {
      handleSwipe('right');
    }
  };

  const handleSubmit = () => {
    if (Object.keys(answers).length !== questions.length) {
      alert('请回答所有题目');
      return;
    }

    // 计算正确答案数量
    let correctCount = 0;
    questions.forEach((q) => {
      const userAnswer = answers[q.id];
      if (userAnswer === q.correct_answer) {
        correctCount++;
      }
    });

    const totalQuestions = questions.length;
    const passed = correctCount >= 8; // 答对8道以上算过关

    setResult({
      passed,
      correctCount,
      totalQuestions,
    });
    setShowResult(true);
    setIsReviewMode(true);
    loadReviewData();
  };

  const loadReviewData = () => {
    // 在复习模式下，标记正确答案和错误答案
    const reviewQuestions = questions.map((q) => {
      const userAnswer = answers[q.id];
      const isCorrect = userAnswer === q.correct_answer;
      return {
        ...q,
        user_answer: userAnswer,
        is_correct: isCorrect,
      };
    });
    setQuestions(reviewQuestions);
  };

  const handleNextLevel = () => {
    if (currentLevel < 4) {
      setCurrentLevel(currentLevel + 1);
    }
  };

  const handleRetry = () => {
    loadQuestions(currentLevel);
  };

  if (questions.length === 0) {
    return <div className="quiz-container">加载中...</div>;
  }

  const currentQuestion = questions[currentIndex];
  const selectedAnswer = answers[currentQuestion.id];

  return (
    <div className="quiz-container">
      <h1 className="page-title">答题闯关区 - 第{currentLevel}关</h1>

      {showResult && result && (
        <div className={`result-modal ${result.passed ? 'success' : 'failure'}`}>
          <div className="result-content">
            {result.passed ? (
              <>
                <div className="success-animation">🎉</div>
                <h2>恭喜过关！</h2>
                <p>答对 {result.correctCount} / {result.totalQuestions} 题</p>
                {currentLevel < 4 ? (
                  <button onClick={handleNextLevel} className="btn btn-primary">
                    进入下一关
                  </button>
                ) : (
                  <p className="completion-message">恭喜完成所有关卡！</p>
                )}
              </>
            ) : (
              <>
                <div className="failure-animation">😔</div>
                <h2>再试一次</h2>
                <p>答对 {result.correctCount} / {result.totalQuestions} 题</p>
                <p className="hint">需要答对8题以上才能过关</p>
                <button onClick={handleRetry} className="btn btn-primary">
                  重新挑战
                </button>
              </>
            )}
          </div>
        </div>
      )}

      <div className="quiz-progress">
        <span>题目 {currentIndex + 1} / {questions.length}</span>
        <div className="progress-bar">
          <div
            className="progress-fill"
            style={{ width: `${((currentIndex + 1) / questions.length) * 100}%` }}
          />
        </div>
      </div>

      <div
        className="question-card"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <div className="question-header">
          <span className="question-number">第 {currentIndex + 1} 题</span>
          {selectedAnswer && (
            <span className={`review-badge ${currentQuestion.is_correct ? 'correct' : 'incorrect'}`}>
              {currentQuestion.is_correct ? '✓ 正确' : '✗ 错误'}
            </span>
          )}
        </div>

        <h2 className="question-text">{currentQuestion.question}</h2>

        <div className="options">
          {['A', 'B', 'C', 'D'].map((option) => {
            const optionKey = `option_${option.toLowerCase()}` as keyof Question;
            const optionText = currentQuestion[optionKey] as string;
            const isSelected = selectedAnswer === option;
            const isCorrect = currentQuestion.correct_answer === option;
            // 实时显示对错：如果已选择答案，立即显示正确/错误状态
            const showCorrect = isSelected && isCorrect;
            const showIncorrect = isSelected && !isCorrect;
            // 如果已选择答案，显示正确答案（即使不是用户选择的）
            const showCorrectAnswer = selectedAnswer && isCorrect && !isSelected;

            return (
              <button
                key={option}
                className={`option-btn ${isSelected ? 'selected' : ''} ${showCorrect ? 'correct' : ''} ${showIncorrect ? 'incorrect' : ''} ${showCorrectAnswer ? 'correct-answer' : ''}`}
                onClick={() => handleAnswer(option)}
                disabled={isReviewMode}
              >
                <span className="option-label">{option}.</span>
                <span className="option-text">{optionText}</span>
                {showCorrect && <span className="answer-icon correct-icon">✓</span>}
                {showIncorrect && <span className="answer-icon incorrect-icon">✗</span>}
                {showCorrectAnswer && <span className="answer-icon correct-answer-icon">✓ 正确答案</span>}
              </button>
            );
          })}
        </div>

        <div className="question-nav">
          <button
            onClick={handlePrev}
            disabled={currentIndex === 0}
            className="btn btn-secondary"
          >
            上一题
          </button>
          {!isReviewMode && (
            <button
              onClick={handleSubmit}
              disabled={Object.keys(answers).length !== questions.length}
              className="btn btn-primary"
            >
              提交答案
            </button>
          )}
          <button
            onClick={handleNext}
            disabled={currentIndex === questions.length - 1}
            className="btn btn-secondary"
          >
            下一题
          </button>
        </div>

        <div className="swipe-hint">
          <span>← 左右滑动切换题目 →</span>
        </div>
      </div>
    </div>
  );
};

export default Quiz;

